/**
 * Media blocks: VIDEO, EMBED
 * Handles video embeds (YouTube/Vimeo) and generic iframe embeds.
 */

import { registerBlockRenderer } from './registry';
import {
  escapeHtml,
  createBlockWrapper,
  isUrlSafe,
  extractVideoId,
  buildVideoEmbedUrl,
  isAllowedEmbedDomain,
  getIframeSandbox,
  getAspectRatioPadding,
} from './utils';
import type { VideoData, EmbedData } from './types';

// =============================================================================
// VIDEO BLOCK
// =============================================================================

registerBlockRenderer('video', (data: unknown, container: HTMLElement) => {
  const videoData = data as VideoData;
  const wrapper = createBlockWrapper('video');

  // Validate URL
  if (!videoData.url || !isUrlSafe(videoData.url)) {
    wrapper.innerHTML = '<div class="pms-video-error">Invalid video URL</div>';
    container.appendChild(wrapper);
    return;
  }

  // Extract video ID and platform
  const videoInfo = extractVideoId(videoData.url);
  if (!videoInfo) {
    wrapper.innerHTML = '<div class="pms-video-error">Unsupported video platform. Only YouTube and Vimeo are supported.</div>';
    container.appendChild(wrapper);
    return;
  }

  const { platform, id } = videoInfo;
  const privacyEnhanced = videoData.privacyEnhanced !== false; // Default true
  const aspectRatio = videoData.aspectRatio || '16:9';
  const padding = getAspectRatioPadding(aspectRatio);

  // Build secure embed URL (never use the raw user URL)
  const embedUrl = buildVideoEmbedUrl(platform, id, privacyEnhanced);

  // Get thumbnail for lazy loading
  const thumbnailUrl = platform === 'youtube'
    ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
    : null; // Vimeo thumbnails require API call, skip for now

  let html = '';

  // Optional title
  if (videoData.title) {
    html += `<div class="pms-video-header">
      <h3 class="pms-video-title">${escapeHtml(videoData.title)}</h3>
    </div>`;
  }

  // Video container with aspect ratio
  html += `<div class="pms-video-container" style="padding-bottom: ${padding}">`;

  if (thumbnailUrl) {
    // Lazy load: show thumbnail with play button, load iframe on click
    html += `
      <div class="pms-video-thumbnail" data-embed-url="${escapeHtml(embedUrl)}">
        <img src="${escapeHtml(thumbnailUrl)}" alt="${escapeHtml(videoData.title || 'Video thumbnail')}" loading="lazy" decoding="async" />
        <button class="pms-video-play" aria-label="Play video">
          <svg viewBox="0 0 68 48" width="68" height="48">
            <path class="pms-video-play-bg" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"></path>
            <path class="pms-video-play-icon" d="M 45,24 27,14 27,34"></path>
          </svg>
        </button>
      </div>
    `;
  } else {
    // No thumbnail available, show iframe directly
    html += `
      <iframe
        src="${escapeHtml(embedUrl)}"
        title="${escapeHtml(videoData.title || 'Video')}"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        loading="lazy"
      ></iframe>
    `;
  }

  html += '</div>';

  // Optional description
  if (videoData.description) {
    html += `<div class="pms-video-description">${escapeHtml(videoData.description)}</div>`;
  }

  wrapper.innerHTML = html;

  // Add click handler for thumbnail -> iframe swap
  if (thumbnailUrl) {
    const thumbnail = wrapper.querySelector('.pms-video-thumbnail') as HTMLElement;
    if (thumbnail) {
      thumbnail.addEventListener('click', () => {
        const videoContainer = thumbnail.parentElement;
        if (videoContainer) {
          const iframe = document.createElement('iframe');
          iframe.src = embedUrl + '&autoplay=1';
          iframe.title = videoData.title || 'Video';
          iframe.frameBorder = '0';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          videoContainer.replaceChild(iframe, thumbnail);
        }
      });
    }
  }

  container.appendChild(wrapper);
});

// =============================================================================
// EMBED BLOCK
// =============================================================================

registerBlockRenderer('embed', (data: unknown, container: HTMLElement) => {
  const embedData = data as EmbedData;
  const wrapper = createBlockWrapper('embed');

  // Validate URL
  if (!embedData.url || !isUrlSafe(embedData.url)) {
    wrapper.innerHTML = '<div class="pms-embed-error">Invalid embed URL</div>';
    container.appendChild(wrapper);
    return;
  }

  // Check domain allowlist
  if (!isAllowedEmbedDomain(embedData.url)) {
    wrapper.innerHTML = `<div class="pms-embed-error">
      <p>This domain is not allowed for embedding.</p>
      <p class="pms-embed-error-hint">Allowed: Figma, CodePen, CodeSandbox, Calendly, Typeform, Loom, Miro, Airtable, Notion, Google Docs/Forms/Slides, Canva, Pitch, Prezi, Spotify, SoundCloud</p>
    </div>`;
    container.appendChild(wrapper);
    return;
  }

  // Require title for accessibility
  if (!embedData.title) {
    wrapper.innerHTML = '<div class="pms-embed-error">Embed requires a title for accessibility</div>';
    container.appendChild(wrapper);
    return;
  }

  const aspectRatio = embedData.aspectRatio || '16:9';
  const allowFullscreen = embedData.allowFullscreen !== false; // Default true
  const sandbox = getIframeSandbox(embedData.url);

  let containerStyle = '';
  if (embedData.height) {
    containerStyle = `height: ${escapeHtml(embedData.height)}`;
  } else {
    containerStyle = `padding-bottom: ${getAspectRatioPadding(aspectRatio)}`;
  }

  const html = `
    <div class="pms-embed-container" style="${containerStyle}">
      <iframe
        src="${escapeHtml(embedData.url)}"
        title="${escapeHtml(embedData.title)}"
        frameborder="0"
        sandbox="${sandbox}"
        ${allowFullscreen ? 'allowfullscreen' : ''}
        loading="lazy"
      ></iframe>
    </div>
  `;

  wrapper.innerHTML = html;
  container.appendChild(wrapper);
});
